import { Component, computed, effect, ElementRef, inject, input, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserStoreService } from '@user-store';
import { forkJoin } from 'rxjs';
import { NotificationReadsService } from '../notification-reads/notification-reads.service';
import { UserNotificationsService } from './user-notifications.service';
import { App, PlausibleService, Role, SummaryOfTheYearService } from '@agilox/common';

@Component({
	selector: 'user-notifications',
	templateUrl: './user-notifications.component.html',
	standalone: false,
})
export class UserNotificationsComponent {
	private userStoreService: UserStoreService = inject(UserStoreService);
	private userNotificationsService: UserNotificationsService = inject(UserNotificationsService);
	private notificationReadsService: NotificationReadsService = inject(NotificationReadsService);
	private plausibleService: PlausibleService = inject(PlausibleService);
	private readonly summaryService = inject(SummaryOfTheYearService);

	app = input<App>(App.analytics);

	articlesDiv = viewChild<ElementRef | undefined>('articles');

	initialLoading = computed(() => this.userNotificationsService.state().isInitialLoading);

	notifications = computed(() => this.userNotificationsService.state().notifications);

	offcanvasOpen = computed(() => this.userNotificationsService.state().isOpen);

	isSummaryButtonVisible = computed(
		() =>
			new Date().getMonth() === 0 &&
			this.userVehicles().length > 0 &&
			this.userStoreService.currentUser()?.role === Role.customer &&
			(this.app() === App.analytics || this.app() === App.my)
	);

	constructor() {
		effect(() => {
			const open = this.offcanvasOpen();
			setTimeout(() => {
				if (this.articlesDiv()?.nativeElement) {
					this.articlesDiv()!.nativeElement.scrollTop = 0;
				}
				if (open) {
					this.plausibleService.eventEmitter('click: open notifications', this.app());
				}
			});
		});
	}

	closeOffcanvas() {
		this.userNotificationsService.toggleNotificationsOffcanvas();

		const unreadNotifications = this.notifications()?.filter((n) => !n.read) ?? [];
		const unreadNotificationIds = unreadNotifications.map((n) => n.id);
		const userId = this.userStoreService.currentUser()!.id;

		const requests = unreadNotificationIds.map((id) =>
			this.notificationReadsService.createNotificationRead({ userId, notificationId: id })
		);

		forkJoin(requests).subscribe({
			next: () => {
				this.userNotificationsService.setStateToRead();
			},
		});
	}

	openSummary(): void {
		this.summaryService.isSummaryOpen.set(true);
	}

	userVehicles = toSignal(this.summaryService.getVehicles(), { initialValue: [] });

	protected readonly year = this.summaryService.year;
}
